import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Mail, ShieldCheck } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/api/auth/admin/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Access denied.'); return; }
      sessionStorage.setItem('adminToken', data.token);
      navigate('/admin/leads');
    } catch {
      setError('Unable to reach server. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "-apple-system,'Segoe UI',Arial,sans-serif" }}>

      {/* Background grid */}
      <div style={{ position: 'fixed', inset: 0, backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(37,99,235,0.06) 1px,transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

      <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src="/tuly.png" alt="Tuly" style={{ height: '32px', width: 'auto', marginBottom: '10px' }} />
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', margin: 0 }}>Admin Portal</p>
        </div>

        {/* Card */}
        <div style={{ background: '#ffffff', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>

          {/* Top accent */}
          <div style={{ height: '3px', background: 'linear-gradient(90deg,#2563eb,#60a5fa,#2563eb)' }} />

          <div style={{ padding: '36px 36px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <ShieldCheck size={20} color="#2563eb" />
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0a0f1e', letterSpacing: '-0.02em' }}>Sign in</h1>
            </div>
            <p style={{ margin: '0 0 28px', fontSize: '13px', color: '#64748b' }}>Enter your admin email to access the dashboard.</p>

            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
                Email address
              </label>
              <div style={{ position: 'relative', marginBottom: '20px' }}>
                <Mail size={15} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@tuly.app"
                  required
                  style={{ width: '100%', padding: '12px 14px 12px 40px', border: `1px solid ${error ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '2px', fontSize: '14px', color: '#0f172a', background: error ? '#fef2f2' : '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderLeft: '3px solid #ef4444', padding: '10px 14px', marginBottom: '20px', fontSize: '13px', color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim()}
                style={{ width: '100%', padding: '13px', background: loading || !email.trim() ? '#94a3b8' : '#0a0f1e', color: '#ffffff', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.04em', cursor: loading || !email.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}
              >
                {loading ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Signing in…</> : 'Sign in →'}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>
          Access restricted to authorised Tuly administrators.
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
